import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, CheckCircle2, ChevronDown, CloudOff, Loader2, RefreshCw, RotateCcw, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import { Button } from '@/components/ui/button';
import { STRIP_TONES } from '@/components/stripboard/Strip';
import * as queue from '@/lib/offlineQueue';
import { EVENTS, track } from '@/lib/analytics';
import { cn } from '@/lib/utils';

const API = API_BASE;

const SHOT_STATUS = [
  { key: 'shot',          label: 'Shot',    cls: 'bg-amber-500 text-white',  ring: 'ring-amber-500' },
  { key: 'covered',       label: 'Covered', cls: 'bg-green-600 text-white',  ring: 'ring-green-600' },
  { key: 'pickup_needed', label: 'Pickup',  cls: 'bg-red-600 text-white',    ring: 'ring-red-600' },
];
const STATUS_LABEL = { planned: 'Planned', shot: 'Shot', covered: 'Covered', pickup_needed: 'Pickup', dropped: 'Dropped' };

async function sendWrite({ method, url, body }) {
  return axios({ method, url: `${API}${url}`, data: body, headers: getAuthHeaders(), timeout: 15000 });
}

/**
 * On-set coverage mode (M3.4): one shoot day on a phone. Tap to mark shots,
 * type a take number, jot a note. Optimistic, with an offline queue.
 * Route: /workspaces/:workspaceId/films/:filmId/days/:scheduleId/coverage
 * The /set route (no schedule) picks today's active day.
 */
export default function CoveragePage() {
  const { workspaceId, filmId, scheduleId: routeScheduleId } = useParams();
  const navigate = useNavigate();
  const [scheduleId, setScheduleId] = useState(routeScheduleId || null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [pending, setPending] = useState(queue.pendingCount());
  const [flushing, setFlushing] = useState(false);
  const noteTimers = useRef({});
  const boardUrl = workspaceId ? `/workspaces/${workspaceId}/films/${filmId}/board` : `/films/${filmId}/board`;

  /* Resolve "today" when no schedule in the route. */
  useEffect(() => {
    if (routeScheduleId) { setScheduleId(routeScheduleId); return; }
    axios.get(`${API}/films/${filmId}/schedules`, { headers: getAuthHeaders() }).then((r) => {
      const days = r.data?.schedules || [];
      const today = new Date().toISOString().slice(0, 10);
      const pick = days.find((d) => d.status === 'active') || days.find((d) => d.start_date === today) || days.find((d) => d.status !== 'completed' && d.scene_count > 0) || days[0];
      if (pick) setScheduleId(pick.id);
      else { setError('No shoot days yet. Build days on the board first.'); setLoading(false); }
    }).catch(() => { setError('Could not load shoot days'); setLoading(false); });
  }, [filmId, routeScheduleId]);

  const load = useCallback(async () => {
    if (!scheduleId) return;
    try {
      const r = await axios.get(`${API}/films/${filmId}/schedules/${scheduleId}/coverage`, { headers: getAuthHeaders() });
      setData(r.data);
      setError(null);
      setOpen((o) => Object.keys(o).length ? o : Object.fromEntries(r.data.scenes.filter((s) => s.status !== 'done').slice(0, 2).map((s) => [s.id, true])));
    } catch (err) {
      if (!data) setError(err?.response?.data?.detail || 'Could not load the day');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filmId, scheduleId]);

  useEffect(() => { load(); }, [load]);

  /* Network + queue */
  const flushQueue = useCallback(async () => {
    if (!navigator.onLine || queue.pendingCount() === 0) return;
    setFlushing(true);
    const r = await queue.flush(sendWrite);
    setFlushing(false);
    if (r.sent) { toast.success(`${r.sent} update${r.sent > 1 ? 's' : ''} synced`); load(); }
    if (r.failed) toast.error(`${r.failed} update${r.failed > 1 ? 's' : ''} could not be applied`);
  }, [load]);

  useEffect(() => {
    const on = () => { setOnline(true); flushQueue(); };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    const unsub = queue.subscribe(setPending);
    flushQueue();
    const t = setInterval(flushQueue, 20000);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); unsub(); clearInterval(t); };
  }, [flushQueue]);

  /* Optimistic write */
  const write = async (method, url, body, apply) => {
    apply();
    if (!navigator.onLine) { queue.enqueue({ method, url, body }); return; }
    try {
      await sendWrite({ method, url, body });
    } catch (err) {
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        toast.error(err?.response?.data?.detail || 'Update rejected');
        load();
      } else {
        queue.enqueue({ method, url, body });
      }
    }
  };

  const setShot = (scene, shot, patch) => {
    const merged = { ...shot, ...patch };
    write('put', `/shots/${shot.id}`, {
      status: merged.status, final_take_number: merged.final_take_number ?? undefined, notes: merged.notes ?? undefined,
    }, () => {
      setData((d) => ({ ...d, scenes: d.scenes.map((s) => (s.id !== scene.id ? s : { ...s, shots: s.shots.map((x) => (x.id === shot.id ? merged : x)) })) }));
      if (patch.status) track(EVENTS.SHOT_STATUS_UPDATED, { shot_id: shot.id, status: patch.status, film_id: filmId, source: 'coverage' });
    });
  };
  const toggleShotStatus = (scene, shot, key) => setShot(scene, shot, { status: shot.status === key ? 'planned' : key });
  const setShotNote = (scene, shot, field, value) => {
    setData((d) => ({ ...d, scenes: d.scenes.map((s) => (s.id !== scene.id ? s : { ...s, shots: s.shots.map((x) => (x.id === shot.id ? { ...x, [field]: value } : x)) })) }));
    clearTimeout(noteTimers.current[shot.id + field]);
    noteTimers.current[shot.id + field] = setTimeout(() => setShot(scene, { ...shot, [field]: value }, {}), 700);
  };
  const setSceneStatus = (scene, status) => {
    write('put', `/scenes/${scene.id}`, { status }, () => {
      setData((d) => ({ ...d, scenes: d.scenes.map((s) => (s.id === scene.id ? { ...s, status } : s)) }));
    });
  };

  const totals = useMemo(() => {
    if (!data) return null;
    let shots = 0, covered = 0, shot = 0, pickups = 0, done = 0;
    data.scenes.forEach((s) => {
      s.shots.forEach((x) => { if (x.status !== 'dropped') shots += 1; if (x.status === 'covered') covered += 1; if (x.status === 'shot') shot += 1; if (x.status === 'pickup_needed') pickups += 1; });
      if (s.status === 'shot' || s.status === 'done') done += 1;
    });
    return { shots, covered, shot, pickups, done, scenes: data.scenes.length };
  }, [data]);

  if (loading && !data) return <div className="p-4 space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-20 rounded-2xl bg-muted/50 animate-pulse" />)}</div>;
  if (error && !data) {
    return <div className="p-4 max-w-md mx-auto"><Link to={boardUrl} className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-3"><ArrowLeft className="size-3" /> Board</Link><div className="rounded-2xl border border-border p-8 text-center text-sm text-muted-foreground">{error}</div></div>;
  }

  const pct = totals.shots ? Math.round((totals.covered / totals.shots) * 100) : 0;

  return (
    <div className="max-w-xl mx-auto px-3 pb-24 pt-3 space-y-3" data-testid="coverage-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 -mx-3 px-3 pt-1 pb-2 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center gap-2">
          <Link to={boardUrl} className="text-muted-foreground hover:text-foreground" aria-label="Back to board"><ArrowLeft className="size-5" /></Link>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold truncate">{data.schedule.name}</div>
            <div className="text-[11px] text-muted-foreground">{data.schedule.start_date || ''} · {totals.done}/{totals.scenes} scenes · {totals.covered}/{totals.shots} shots covered{totals.pickups ? ` · ${totals.pickups} pickups` : ''}</div>
          </div>
          <button type="button" onClick={load} className="text-muted-foreground hover:text-foreground" aria-label="Refresh"><RefreshCw className="size-4" /></button>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-green-600 transition-all" style={{ width: `${pct}%` }} /></div>
        {(!online || pending > 0) && (
          <div className={cn('mt-2 flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs', online ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300' : 'bg-muted text-muted-foreground')} data-testid="coverage-offline-banner">
            {online ? <CloudOff className="size-3.5" /> : <WifiOff className="size-3.5" />}
            {!online ? `Offline — ${pending} update${pending === 1 ? '' : 's'} saved on this phone, will sync when back` : `${pending} update${pending === 1 ? '' : 's'} waiting to sync`}
            {online && pending > 0 && <button type="button" onClick={flushQueue} className="ml-auto underline" disabled={flushing}>{flushing ? 'Syncing…' : 'Sync now'}</button>}
          </div>
        )}
      </div>

      {data.scenes.length === 0 && <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No scenes on this day.</div>}

      {data.scenes.map((scene) => {
        const tone = STRIP_TONES[scene.strip] || STRIP_TONES.neutral;
        const isOpen = !!open[scene.id];
        const c = scene.shots.reduce((a, x) => { a[x.status] = (a[x.status] || 0) + 1; return a; }, {});
        const live = scene.shots.filter((x) => x.status !== 'dropped').length;
        const sceneDone = scene.status === 'shot' || scene.status === 'done';
        return (
          <section key={scene.id} className={cn('rounded-2xl border border-border overflow-hidden', tone.bg)} data-testid={`cov-scene-${scene.id}`}>
            <button type="button" onClick={() => setOpen((o) => ({ ...o, [scene.id]: !isOpen }))} className="w-full flex items-stretch text-left">
              <span className={cn('w-1.5 shrink-0', tone.bar)} />
              <div className="flex-1 min-w-0 px-3 py-2.5 flex items-center gap-3">
                <span className="text-xl font-bold tabular-nums w-10 shrink-0">{scene.scene_number}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{scene.scene_name || scene.location || 'Scene'}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {[scene.interior_exterior === 'interior' ? 'INT' : scene.interior_exterior === 'exterior' ? 'EXT' : null, (scene.time_of_day || '').toUpperCase() || null, scene.location].filter(Boolean).join(' · ')}
                    {scene.cast?.length ? ` · ${scene.cast.join(', ')}` : ''}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={cn('text-sm font-bold tabular-nums', (c.covered || 0) === live && live > 0 ? 'text-green-600' : '')}>{c.covered || 0}/{live}</div>
                  <div className="text-[10px] text-muted-foreground">{sceneDone ? <span className="text-green-600 font-semibold">done</span> : c.pickup_needed ? <span className="text-red-600">{c.pickup_needed} pickup</span> : 'covered'}</div>
                </div>
                <ChevronDown className={cn('size-4 text-muted-foreground transition-transform shrink-0', isOpen && 'rotate-180')} />
              </div>
            </button>

            {isOpen && (
              <div className="bg-card border-t border-border divide-y divide-border">
                {scene.shots.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground italic">No shots planned for this scene.</p>}
                {scene.shots.map((shot) => (
                  <div key={shot.id} className={cn('px-3 py-2.5 space-y-2', shot.status === 'dropped' && 'opacity-50')} data-testid={`cov-shot-${shot.id}`}>
                    <div className="flex items-start gap-2">
                      <span className="font-mono font-bold text-sm w-10 shrink-0 pt-0.5">{shot.shot_code}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm leading-snug">{shot.description || '—'}</div>
                        <div className="text-[11px] text-muted-foreground">{[shot.shot_type, shot.camera_movement, shot.lens].filter(Boolean).join(' · ')}{shot.cast?.length ? ` · ${shot.cast.join(', ')}` : ''}</div>
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0 pt-1">{STATUS_LABEL[shot.status] || shot.status}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {SHOT_STATUS.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => toggleShotStatus(scene, shot, s.key)}
                          className={cn('h-10 flex-1 rounded-xl text-sm font-semibold transition-colors border',
                            shot.status === s.key ? cn(s.cls, 'border-transparent') : 'bg-background border-border text-foreground active:bg-accent')}
                          aria-pressed={shot.status === s.key}
                          data-testid={`cov-${s.key}-${shot.id}`}
                        >
                          {shot.status === s.key && <Check className="size-4 inline mr-1 -mt-0.5" />}{s.label}
                        </button>
                      ))}
                      <input
                        value={shot.final_take_number || ''}
                        onChange={(e) => setShotNote(scene, shot, 'final_take_number', e.target.value)}
                        placeholder="Take"
                        inputMode="numeric"
                        className="h-10 w-16 rounded-xl border border-border bg-background px-2 text-sm text-center"
                        aria-label="Final take"
                      />
                    </div>
                    <input
                      value={shot.notes || ''}
                      onChange={(e) => setShotNote(scene, shot, 'notes', e.target.value)}
                      placeholder="Note (e.g. take 4 best, boom in frame on 2)"
                      className="h-9 w-full rounded-xl border border-border bg-background px-3 text-sm"
                      aria-label="Shot note"
                    />
                  </div>
                ))}
                <div className="px-3 py-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSceneStatus(scene, sceneDone ? 'ready' : 'shot')}
                    className={cn('h-10 flex-1 rounded-xl text-sm font-semibold border inline-flex items-center justify-center gap-1.5',
                      sceneDone ? 'bg-green-600 text-white border-transparent' : 'bg-background border-border')}
                    data-testid={`cov-scene-done-${scene.id}`}
                  >
                    {sceneDone ? <><CheckCircle2 className="size-4" /> Scene wrapped</> : 'Wrap scene'}
                  </button>
                  {sceneDone && <button type="button" onClick={() => setSceneStatus(scene, 'ready')} className="h-10 px-3 rounded-xl border border-border text-xs text-muted-foreground inline-flex items-center gap-1"><RotateCcw className="size-3.5" /> Reopen</button>}
                </div>
              </div>
            )}
          </section>
        );
      })}

      <div className="text-center text-[11px] text-muted-foreground pt-2">
        {flushing && <Loader2 className="size-3 inline animate-spin mr-1" />}
        Taps save instantly. Add this page to your home screen for the fastest set experience.
      </div>
    </div>
  );
}
